import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./DashboardLayout";
import { Button } from "../components/ui/button";
import {
  RefreshCw,
  Loader2,
  Mail,
  Phone,
  Globe,
  Clock,
  CheckCircle2,
  Trash2,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

function relativeTime(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function InboundLeads() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active"); // active | all | handled
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/v1/contact-submissions");
      setSubmissions(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleHandled = async (sub) => {
    setBusyId(sub.id);
    try {
      const r = await api.patch(`/v1/contact-submissions/${sub.id}`, {
        handled: !sub.handled,
      });
      setSubmissions((list) => list.map((s) => (s.id === sub.id ? r.data : s)));
      toast.success(r.data.handled ? "Marked handled" : "Reopened");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const deleteSub = async (sub) => {
    if (!window.confirm(`Delete submission from ${sub.email}? This cannot be undone.`)) return;
    setBusyId(sub.id);
    try {
      await api.delete(`/v1/contact-submissions/${sub.id}`);
      setSubmissions((list) => list.filter((s) => s.id !== sub.id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const filtered = submissions.filter((s) => {
    if (filter === "active") return !s.handled;
    if (filter === "handled") return s.handled;
    return true;
  });

  const counts = {
    active: submissions.filter((s) => !s.handled).length,
    handled: submissions.filter((s) => s.handled).length,
    all: submissions.length,
  };

  return (
    <div data-testid="inbound-leads-page">
      <PageHeader
        eyebrow="Module 08 · Inbound Leads"
        title="Schedule-a-call submissions"
        description="Every prospect who submitted the /schedule form (from the hero, footer, email CTA, or pricing-tier CTA). Mark handled when you've replied; delete to clear spam."
        actions={
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            className="rounded-sm h-10 border-zinc-400 hover:bg-zinc-50"
            data-testid="leads-refresh-button"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        }
        testid="leads-header"
      />

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 space-y-4">
        {/* Filter tabs */}
        <div className="flex gap-1 border-b-2 border-zinc-200" data-testid="leads-filter-tabs">
          {[
            { key: "active",  label: "Active",  count: counts.active },
            { key: "handled", label: "Handled", count: counts.handled },
            { key: "all",     label: "All",     count: counts.all },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border-b-2 -mb-0.5 transition-colors ${
                filter === t.key
                  ? "border-blue-700 text-blue-700"
                  : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}
              data-testid={`leads-tab-${t.key}`}
            >
              {t.label} <span className="font-mono text-zinc-400 ml-1">{t.count}</span>
            </button>
          ))}
        </div>

        {/* List */}
        {loading && filtered.length === 0 && (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-zinc-400" /></div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="bg-white border-2 border-dashed border-zinc-300 py-16 text-center text-sm text-zinc-500" data-testid="leads-empty">
            {filter === "active" ? "Inbox zero. No active leads right now." : "Nothing here."}
          </div>
        )}

        <div className="space-y-3">
          {filtered.map((s) => (
            <article
              key={s.id}
              className={`bg-white border-2 ${s.handled ? "border-zinc-300 opacity-60" : "border-zinc-950"} p-4 sm:p-5`}
              data-testid={`leads-card-${s.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-lg font-black tracking-tight truncate">
                      {s.name}
                    </h3>
                    {s.handled && (
                      <span className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-300 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                        <CheckCircle2 className="w-3 h-3" /> Handled
                      </span>
                    )}
                    {s.source_audit_id && (
                      <span className="text-[10px] font-mono bg-zinc-100 px-1.5 py-0.5 border border-zinc-300 text-zinc-700">
                        ref:{s.source_audit_id.slice(0, 8)}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                    <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1.5 text-blue-700 hover:text-blue-900 font-mono text-xs">
                      <Mail className="w-3 h-3" /> {s.email}
                    </a>
                    {s.phone && (
                      <span className="inline-flex items-center gap-1.5 text-zinc-700 text-xs">
                        <Phone className="w-3 h-3" /> {s.phone}
                      </span>
                    )}
                    {s.business && (
                      <span className="text-zinc-700 text-xs">{s.business}</span>
                    )}
                    {s.website && (
                      <a href={s.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 font-mono text-xs truncate">
                        <Globe className="w-3 h-3 shrink-0" /> {s.website}
                        <ExternalLink className="w-2.5 h-2.5 opacity-60 shrink-0" />
                      </a>
                    )}
                  </div>
                  {s.preferred_time && (
                    <div className="mt-2 text-xs text-zinc-600">
                      <span className="font-bold">Preferred time:</span> {s.preferred_time}
                    </div>
                  )}
                  {s.message && (
                    <p className="mt-3 text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap border-l-4 border-zinc-200 pl-3">
                      {s.message}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <div className="text-[11px] text-zinc-500 font-mono inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {relativeTime(s.created_at)}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => toggleHandled(s)}
                      disabled={busyId === s.id}
                      className={`h-8 rounded-sm text-[10px] font-bold uppercase tracking-wider ${
                        s.handled
                          ? "bg-zinc-200 hover:bg-zinc-300 text-zinc-800"
                          : "bg-emerald-600 hover:bg-emerald-700 text-white"
                      }`}
                      data-testid={`leads-toggle-${s.id}`}
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      {s.handled ? "Reopen" : "Mark Handled"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => deleteSub(s)}
                      disabled={busyId === s.id}
                      className="h-8 rounded-sm text-[10px] font-bold uppercase tracking-wider border-red-400 text-red-700 hover:bg-red-50"
                      data-testid={`leads-delete-${s.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
